import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { DateTools, Table, TTableColumn } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { StatusLabel } from '@/ui-kit/status-label/StatusLabel';
import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { getStatusByLabel } from '@/types/colors.types';

import { getBulkCardCreations } from '@/store/actions/bulkCardCreations.actions';

import { IBulkCard } from '@/features/cards/store/models/bulk-card.model';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';

import { DetailAction } from '@/features/cards/components/bulkCardCreation/tableActions/DetailAction';
import { DownloadReportAction } from '@/features/cards/components/bulkCardCreation/tableActions/DownloadReportAction';
import { ErrorReportAction } from '@/features/cards/components/bulkCardCreation/tableActions/ErrorReportAction';

import { useRoutepaths } from '@/router/hooks/useRoutePaths';
import { TableSkeleton } from '@/ui-kit/loaders/TableSkeleton';
import { Page } from '@/ui-kit/templates/Page';

const tableColumns = ['orderId', 'createDate', 'nbCards', 'gpsId', 'status', 'actions'] as const;

type TBulkCardCell = Record<(typeof tableColumns)[number], ReactNode>;

export const BulkCardCreation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const PathRoute = useRoutepaths();

  const bulkCardCreations = useAppSelector((state) =>
    state.bulkCardCreation.bulkCardCreations.length > 0
      ? state.bulkCardCreation.bulkCardCreations.sort((a: IBulkCard, b: IBulkCard) => {
          if (!a['createdAt']) return -1;
          if (!b['createdAt']) return 1;
          return DateTools.compare(DateTools.createDate(b?.createdAt), DateTools.createDate(a?.createdAt));
        })
      : state.bulkCardCreation.bulkCardCreations,
  );
  const isLoading = useAppSelector((state) => state.bulkCardCreation.isLoading);

  useEffect(() => {
    dispatch(getBulkCardCreations());
  }, [dispatch]);

  const headers: TTableColumn<TBulkCardCell>[] = tableColumns.map((field) => ({
    name: field,
    field,
    label: t(`pages.administration.bulkCards.table.${field}`),
  }));

  const rows = bulkCardCreations?.map((bulkCardCreation: IBulkCard) => ({
    orderId: <span className="typo-body-regular-small text-secondary">{bulkCardCreation.orderId}</span>,
    createDate: DateTools.format(bulkCardCreation.createdAt) || '-',
    nbCards: bulkCardCreation.number,
    gpsId: bulkCardCreation.cardPrint,
    status:
      bulkCardCreation.status && bulkCardCreation.orderId ? (
        <StatusLabel
          label={DashboardHelpers.getStatusLabelTranslation(bulkCardCreation.status)}
          status={getStatusByLabel(bulkCardCreation.status)}
        />
      ) : (
        '-'
      ),
    actions: (
      <div className="flex gap-4">
        <ErrorReportAction bulkCardCreation={bulkCardCreation} />
        <DetailAction bulkCardCreation={bulkCardCreation} />
        <DownloadReportAction bulkCardCreation={bulkCardCreation} />
      </div>
    ),
  }));

  return (
    <Page
      actions={[
        {
          id: 'add-bulk-cards-btn',
          label: t('pages.administration.bulkCards.title'),
          icon: 'add',
          onClick: () =>
            navigate(
              generatePath(PathRoute.app.root, {
                '*': generatePath(PathRoute.app.bulkCards.others, { '*': PathRoute.app.bulkCards.create }),
              }),
            ),
        },
      ]}
    >
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={headers}
          rows={rows}
          className="bg-primary"
          emptyComponent={
            <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" className="p-6" />
          }
        />
      )}
    </Page>
  );
};
